<div class="page">
    <!-- ===================== HEADER ===================== -->
    <div class="page-head">
        <div>
            <h2>Partners</h2>
            <p>Select a partner to manage its gateway, products, messaging and mode.</p>
        </div>
        <p-button label="Add Partner" icon="pi pi-plus" (onClick)="openAdd()" />
    </div>

    <!-- ===================== LIST ===================== -->
    <div class="card list-card">
        <div class="list-head">
            <p-iconfield iconPosition="left" class="search-field">
                <p-inputicon styleClass="pi pi-search" />
                <input pInputText type="text" placeholder="Search name or partner code..."
                    [ngModel]="search()" [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="search.set($event)">
            </p-iconfield>
            <div class="status-tabs">
                <button type="button" [class.on]="statusFilter() === 'all'" (click)="statusFilter.set('all')">All</button>
                <button type="button" [class.on]="statusFilter() === 'verified'" (click)="statusFilter.set('verified')">Verified</button>
                <button type="button" [class.on]="statusFilter() === 'pending'" (click)="statusFilter.set('pending')">Pending</button>
            </div>
        </div>

        <p-table [value]="filtered()" [loading]="listLoading()" dataKey="id" [rows]="10"
            [paginator]="filtered().length > 10" [rowsPerPageOptions]="[10, 25, 50]"
            styleClass="p-datatable-sm" [rowHover]="true" [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
            <ng-template pTemplate="header">
                <tr>
                    <th>Partner</th>
                    <th>Contact</th>
                    <th>Location</th>
                    <th class="ta-c">KYC</th>
                    <th class="ta-r">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-p>
                <!-- The whole row opens the partner, the same as the clients list. -->
                <tr class="clickable" (click)="openPartner(p)">
                    <td>
                        <div class="p-cell">
                            <p-avatar [label]="initialsOf(p.partnerName)" shape="circle" size="large"
                                styleClass="avatar-brand" />
                            <div class="p-meta">
                                <strong>{{ p.partnerName }}</strong>
                                <span class="mono">{{ p.partnerCode || '—' }}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        @if (p.contactPerson) { <div class="sub-line">{{ p.contactPerson }}</div> }
                        @if (p.phone) { <div class="sub-line">{{ p.phone }}</div> }
                        @if (!p.contactPerson && !p.phone) { <span class="sub-line">—</span> }
                    </td>
                    <td>
                        <span class="sub-line">
                            {{ p.city || '—' }}{{ p.state ? ', ' + p.state : '' }}
                        </span>
                    </td>
                    <td class="ta-c">
                        @if (p.kycVerified) {
                            <p-tag value="Verified" severity="success" icon="pi pi-check-circle" />
                        } @else {
                            <p-tag value="Pending" severity="warn" icon="pi pi-clock" />
                        }
                    </td>
                    <td class="ta-r nowrap">
                        <!-- stopPropagation: these sit inside a clickable row. -->
                        <p-button label="Manage" icon="pi pi-cog" [text]="true" size="small"
                            type="button" (onClick)="openPartner(p); $event.stopPropagation()" />
                        <p-button label="Edit" icon="pi pi-pencil" [text]="true" severity="secondary"
                            size="small" type="button" (onClick)="startEdit(p); $event.stopPropagation()" />
                        @if (!p.kycVerified) {
                            <p-button label="Verify" icon="pi pi-verified" [text]="true" size="small"
                                type="button" (onClick)="openVerify(p); $event.stopPropagation()" />
                        }
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="5">
                        <div class="state"><i class="pi pi-inbox"></i><p>No partners match your filter.</p></div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<!-- ===================== ADD / EDIT PARTNER MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true"
    [draggable]="true" [resizable]="false" [dismissableMask]="false"
    [header]="editing() ? 'Edit Partner' : 'Add Partner'"
    styleClass="menu-dialog" [style]="{ width: '820px' }" [breakpoints]="{ '900px': '95vw' }">
    <form [formGroup]="form" (ngSubmit)="save()" class="form-card in-dialog">
        <div class="form-head">
            <div>
                <p class="form-sub">{{ editing() ? 'Update contact details. Code & login stay fixed.' : 'Register a partner and its login in one step.' }}</p>
            </div>
        </div>

        <!-- Partner code (auto preview on add, read-only on edit) -->
        <div class="field code-field">
            <label>Partner Code <span class="muted">(auto)</span></label>
            <div class="code-row">
                <input pInputText type="text" [value]="codeLoading() ? 'Loading…' : (partnerCode() || '—')" readonly>
                @if (!editing()) {
                    <p-button [icon]="codeLoading() ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'" [rounded]="true"
                        [text]="true" severity="secondary" type="button" pTooltip="Refresh preview"
                        (onClick)="loadPreviewCode()" />
                }
            </div>
        </div>

        <h3 class="section-title"><i class="pi pi-building"></i> Organization</h3>
        <div class="grid">
            <div class="field">
                <label>Partner Name <span class="req">*</span></label>
                <input pInputText type="text" formControlName="partnerName" placeholder="Acme Health Pvt Ltd">
                @if (form.get('partnerName')?.touched && form.get('partnerName')?.invalid) {
                    <small class="err">Partner name is required</small>
                }
            </div>
            <div class="field">
                <label>Contact Person</label>
                <input pInputText type="text" formControlName="contactPerson" placeholder="John Doe">
            </div>
            <div class="field">
                <label>Email</label>
                <input pInputText type="email" formControlName="email" placeholder="partner@email.com">
                @if (form.get('email')?.touched && form.get('email')?.invalid) {
                    <small class="err">Enter a valid email</small>
                }
            </div>
            <div class="field">
                <label>Phone <span class="req">*</span></label>
                <input pInputText type="text" formControlName="phone" placeholder="9876543210">
                @if (form.get('phone')?.touched && form.get('phone')?.invalid) {
                    <small class="err">Enter a valid phone number</small>
                }
            </div>
        </div>

        <h3 class="section-title"><i class="pi pi-map-marker"></i> Address</h3>
        <div class="grid">
            <div class="field span-2">
                <label>Address</label>
                <input pInputText type="text" formControlName="address" placeholder="Street, area">
            </div>
            <div class="field">
                <label>City</label>
                <input pInputText type="text" formControlName="city" placeholder="Mumbai">
            </div>
            <div class="field">
                <label>State</label>
                <input pInputText type="text" formControlName="state" placeholder="Maharashtra">
            </div>
            <div class="field">
                <label>Pincode</label>
                <input pInputText type="text" formControlName="pincode" placeholder="400001">
            </div>
        </div>

        <!-- Login & roles only when creating -->
        @if (!editing()) {
            <h3 class="section-title"><i class="pi pi-lock"></i> Login & Roles</h3>
            <div class="grid">
                <div class="field">
                    <label>Username <span class="req">*</span></label>
                    <input pInputText type="text" formControlName="username" placeholder="acme_admin">
                    @if (form.get('username')?.touched && form.get('username')?.invalid) {
                        <small class="err">Username is required</small>
                    }
                </div>
                <div class="field">
                    <label>Password <span class="req">*</span></label>
                    <p-password [toggleMask]="true" [feedback]="false" [fluid]="true"
                        formControlName="password" placeholder="Min 6 characters" />
                    @if (form.get('password')?.touched && form.get('password')?.invalid) {
                        <small class="err">Min 6 characters</small>
                    }
                </div>
                <div class="field span-2">
                    <label>Roles</label>
                    @if (roleOptions().length) {
                        <div class="role-chips">
                            @for (r of roleOptions(); track r.value) {
                                <label class="role-chip" [class.on]="isRoleSelected(+r.value)">
                                    <input type="checkbox" [checked]="isRoleSelected(+r.value)"
                                        (change)="toggleRole(+r.value)">
                                    <i class="pi pi-check"></i> {{ r.label }}
                                </label>
                            }
                        </div>
                    } @else {
                        <small class="muted">No roles available.</small>
                    }
                </div>
            </div>

            <h3 class="section-title"><i class="pi pi-send"></i> Share OTP / Password via</h3>
            <div class="channel-row">
                <label class="channel-chip" [class.on]="form.get('isWhatsApp')?.value">
                    <input type="checkbox" formControlName="isWhatsApp">
                    <i class="pi pi-whatsapp"></i> WhatsApp
                </label>
                <label class="channel-chip" [class.on]="form.get('isSMS')?.value">
                    <input type="checkbox" formControlName="isSMS">
                    <i class="pi pi-comment"></i> SMS
                </label>
                <label class="channel-chip" [class.on]="form.get('isEmail')?.value">
                    <input type="checkbox" formControlName="isEmail">
                    <i class="pi pi-envelope"></i> Email
                </label>
            </div>
            @if (noChannel) {
                <small class="err">Select at least one channel.</small>
            }
        } @else {
            <h3 class="section-title"><i class="pi pi-lock"></i> Login (read-only)</h3>
            <div class="grid">
                <div class="field">
                    <label>Username</label>
                    <input pInputText type="text" formControlName="username" readonly>
                </div>
            </div>
        }

        <div class="form-actions">
            @if (editing()) {
                <p-button label="Cancel" [outlined]="true" type="button" (onClick)="startAdd()" />
            }
            <p-button type="submit" [disabled]="saving()" [loading]="saving()"
                [label]="saving() ? 'Saving...' : (editing() ? 'Update Partner' : 'Add Partner')"
                [icon]="saving() ? '' : 'pi pi-check'" />
        </div>
    </form>
</p-dialog>

<!-- ===================== VERIFY OTP MODAL ===================== -->
<p-dialog [visible]="showOtpModal()" (visibleChange)="showOtpModal.set($event)" [modal]="true"
    [draggable]="true" [resizable]="false" [closable]="true" [dismissableMask]="false"
    [closeOnEscape]="false" (onShow)="onOtpShow()" header="Verify Partner"
    styleClass="menu-dialog" [style]="{ width: '430px' }" [breakpoints]="{ '480px': '94vw' }">
    <div class="otp-body">
        <p class="otp-sub">
            An OTP was sent to verify <strong>{{ partnerLabel() }}</strong>. Enter the 6-digit code below.
        </p>
        <div class="otp-inputs" (paste)="onPaste($event)">
            @for (ctrl of otpArray.controls; track i; let i = $index) {
                <input #otpInput type="text" inputmode="numeric" maxlength="1" class="otp-box"
                    [formControl]="ctrl" (input)="onInput($event, i)" (keydown)="onKeyDown($event, i)">
            }
        </div>
        <div class="otp-resend">
            Didn't get it?
            <button type="button" class="link" (click)="sendOtp(false)" [disabled]="sendingOtp()">
                @if (sendingOtp()) { <i class="pi pi-spin pi-spinner"></i> Sending... } @else { Resend OTP }
            </button>
        </div>
        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" type="button" (onClick)="closeOtpModal()" />
            <p-button label="Verify" type="button" [disabled]="verifying()" [loading]="verifying()"
                (onClick)="verifyPartner()" />
        </div>
    </div>
</p-dialog>
